/**
 * Countries, their dialling code, and one representative timezone each.
 *
 * The timezone is a *seed*, not a claim. For the countries that plainly span
 * several zones — the United States, Brazil, Canada, Russia, Australia — the
 * value is the zone most of the population lives in, and it exists only to
 * pre-fill a picker the therapist can then correct. Anything that has to be
 * right reads the patient's own stored zone.
 *
 * Names are not in this file. `Intl.DisplayNames` already knows every country
 * in every language the interface speaks, so translating 247 of them by hand
 * twice would be work whose only outcome is drift.
 *
 * Kept in sync by hand with `backend/app/domain/services/timezones.py`, which
 * holds the same country → zone table for the fallback the emails apply when a
 * patient has no zone of their own.
 */

export interface Country {
    /** ISO 3166-1 alpha-2. */
    code: string;
    /** E.164 country calling code, with its leading `+`. */
    dialCode: string;
    /** IANA identifier — a first guess at where this country's clocks are. */
    timezone: string;
}

export const COUNTRIES: readonly Country[] = [
    { code: "AD", dialCode: "+376", timezone: "Europe/Andorra" },
    { code: "AE", dialCode: "+971", timezone: "Asia/Dubai" },
    { code: "AF", dialCode: "+93", timezone: "Asia/Kabul" },
    { code: "AG", dialCode: "+1268", timezone: "America/Antigua" },
    { code: "AI", dialCode: "+1264", timezone: "America/Anguilla" },
    { code: "AL", dialCode: "+355", timezone: "Europe/Tirane" },
    { code: "AM", dialCode: "+374", timezone: "Asia/Yerevan" },
    { code: "AO", dialCode: "+244", timezone: "Africa/Luanda" },
    { code: "AQ", dialCode: "+672", timezone: "Antarctica/McMurdo" },
    { code: "AR", dialCode: "+54", timezone: "America/Argentina/Buenos_Aires" },
    { code: "AS", dialCode: "+1684", timezone: "Pacific/Pago_Pago" },
    { code: "AT", dialCode: "+43", timezone: "Europe/Vienna" },
    { code: "AU", dialCode: "+61", timezone: "Australia/Sydney" },
    { code: "AW", dialCode: "+297", timezone: "America/Aruba" },
    { code: "AX", dialCode: "+358", timezone: "Europe/Mariehamn" },
    { code: "AZ", dialCode: "+994", timezone: "Asia/Baku" },
    { code: "BA", dialCode: "+387", timezone: "Europe/Sarajevo" },
    { code: "BB", dialCode: "+1246", timezone: "America/Barbados" },
    { code: "BD", dialCode: "+880", timezone: "Asia/Dhaka" },
    { code: "BE", dialCode: "+32", timezone: "Europe/Brussels" },
    { code: "BF", dialCode: "+226", timezone: "Africa/Ouagadougou" },
    { code: "BG", dialCode: "+359", timezone: "Europe/Sofia" },
    { code: "BH", dialCode: "+973", timezone: "Asia/Bahrain" },
    { code: "BI", dialCode: "+257", timezone: "Africa/Bujumbura" },
    { code: "BJ", dialCode: "+229", timezone: "Africa/Porto-Novo" },
    { code: "BL", dialCode: "+590", timezone: "America/St_Barthelemy" },
    { code: "BM", dialCode: "+1441", timezone: "Atlantic/Bermuda" },
    { code: "BN", dialCode: "+673", timezone: "Asia/Brunei" },
    { code: "BO", dialCode: "+591", timezone: "America/La_Paz" },
    { code: "BQ", dialCode: "+599", timezone: "America/Kralendijk" },
    { code: "BR", dialCode: "+55", timezone: "America/Sao_Paulo" },
    { code: "BS", dialCode: "+1242", timezone: "America/Nassau" },
    { code: "BT", dialCode: "+975", timezone: "Asia/Thimphu" },
    { code: "BW", dialCode: "+267", timezone: "Africa/Gaborone" },
    { code: "BY", dialCode: "+375", timezone: "Europe/Minsk" },
    { code: "BZ", dialCode: "+501", timezone: "America/Belize" },
    { code: "CA", dialCode: "+1", timezone: "America/Toronto" },
    { code: "CC", dialCode: "+61", timezone: "Indian/Cocos" },
    { code: "CD", dialCode: "+243", timezone: "Africa/Kinshasa" },
    { code: "CF", dialCode: "+236", timezone: "Africa/Bangui" },
    { code: "CG", dialCode: "+242", timezone: "Africa/Brazzaville" },
    { code: "CH", dialCode: "+41", timezone: "Europe/Zurich" },
    { code: "CI", dialCode: "+225", timezone: "Africa/Abidjan" },
    { code: "CK", dialCode: "+682", timezone: "Pacific/Rarotonga" },
    { code: "CL", dialCode: "+56", timezone: "America/Santiago" },
    { code: "CM", dialCode: "+237", timezone: "Africa/Douala" },
    { code: "CN", dialCode: "+86", timezone: "Asia/Shanghai" },
    { code: "CO", dialCode: "+57", timezone: "America/Bogota" },
    { code: "CR", dialCode: "+506", timezone: "America/Costa_Rica" },
    { code: "CU", dialCode: "+53", timezone: "America/Havana" },
    { code: "CV", dialCode: "+238", timezone: "Atlantic/Cape_Verde" },
    { code: "CW", dialCode: "+599", timezone: "America/Curacao" },
    { code: "CX", dialCode: "+61", timezone: "Indian/Christmas" },
    { code: "CY", dialCode: "+357", timezone: "Asia/Nicosia" },
    { code: "CZ", dialCode: "+420", timezone: "Europe/Prague" },
    { code: "DE", dialCode: "+49", timezone: "Europe/Berlin" },
    { code: "DJ", dialCode: "+253", timezone: "Africa/Djibouti" },
    { code: "DK", dialCode: "+45", timezone: "Europe/Copenhagen" },
    { code: "DM", dialCode: "+1767", timezone: "America/Dominica" },
    { code: "DO", dialCode: "+1809", timezone: "America/Santo_Domingo" },
    { code: "DZ", dialCode: "+213", timezone: "Africa/Algiers" },
    { code: "EC", dialCode: "+593", timezone: "America/Guayaquil" },
    { code: "EE", dialCode: "+372", timezone: "Europe/Tallinn" },
    { code: "EG", dialCode: "+20", timezone: "Africa/Cairo" },
    { code: "EH", dialCode: "+212", timezone: "Africa/El_Aaiun" },
    { code: "ER", dialCode: "+291", timezone: "Africa/Asmara" },
    { code: "ES", dialCode: "+34", timezone: "Europe/Madrid" },
    { code: "ET", dialCode: "+251", timezone: "Africa/Addis_Ababa" },
    { code: "FI", dialCode: "+358", timezone: "Europe/Helsinki" },
    { code: "FJ", dialCode: "+679", timezone: "Pacific/Fiji" },
    { code: "FK", dialCode: "+500", timezone: "Atlantic/Stanley" },
    { code: "FM", dialCode: "+691", timezone: "Pacific/Chuuk" },
    { code: "FO", dialCode: "+298", timezone: "Atlantic/Faroe" },
    { code: "FR", dialCode: "+33", timezone: "Europe/Paris" },
    { code: "GA", dialCode: "+241", timezone: "Africa/Libreville" },
    { code: "GB", dialCode: "+44", timezone: "Europe/London" },
    { code: "GD", dialCode: "+1473", timezone: "America/Grenada" },
    { code: "GE", dialCode: "+995", timezone: "Asia/Tbilisi" },
    { code: "GF", dialCode: "+594", timezone: "America/Cayenne" },
    { code: "GG", dialCode: "+44", timezone: "Europe/Guernsey" },
    { code: "GH", dialCode: "+233", timezone: "Africa/Accra" },
    { code: "GI", dialCode: "+350", timezone: "Europe/Gibraltar" },
    { code: "GL", dialCode: "+299", timezone: "America/Nuuk" },
    { code: "GM", dialCode: "+220", timezone: "Africa/Banjul" },
    { code: "GN", dialCode: "+224", timezone: "Africa/Conakry" },
    { code: "GP", dialCode: "+590", timezone: "America/Guadeloupe" },
    { code: "GQ", dialCode: "+240", timezone: "Africa/Malabo" },
    { code: "GR", dialCode: "+30", timezone: "Europe/Athens" },
    { code: "GS", dialCode: "+500", timezone: "Atlantic/South_Georgia" },
    { code: "GT", dialCode: "+502", timezone: "America/Guatemala" },
    { code: "GU", dialCode: "+1671", timezone: "Pacific/Guam" },
    { code: "GW", dialCode: "+245", timezone: "Africa/Bissau" },
    { code: "GY", dialCode: "+592", timezone: "America/Guyana" },
    { code: "HK", dialCode: "+852", timezone: "Asia/Hong_Kong" },
    { code: "HN", dialCode: "+504", timezone: "America/Tegucigalpa" },
    { code: "HR", dialCode: "+385", timezone: "Europe/Zagreb" },
    { code: "HT", dialCode: "+509", timezone: "America/Port-au-Prince" },
    { code: "HU", dialCode: "+36", timezone: "Europe/Budapest" },
    { code: "ID", dialCode: "+62", timezone: "Asia/Jakarta" },
    { code: "IE", dialCode: "+353", timezone: "Europe/Dublin" },
    { code: "IL", dialCode: "+972", timezone: "Asia/Jerusalem" },
    { code: "IM", dialCode: "+44", timezone: "Europe/Isle_of_Man" },
    { code: "IN", dialCode: "+91", timezone: "Asia/Kolkata" },
    { code: "IO", dialCode: "+246", timezone: "Indian/Chagos" },
    { code: "IQ", dialCode: "+964", timezone: "Asia/Baghdad" },
    { code: "IR", dialCode: "+98", timezone: "Asia/Tehran" },
    { code: "IS", dialCode: "+354", timezone: "Atlantic/Reykjavik" },
    { code: "IT", dialCode: "+39", timezone: "Europe/Rome" },
    { code: "JE", dialCode: "+44", timezone: "Europe/Jersey" },
    { code: "JM", dialCode: "+1876", timezone: "America/Jamaica" },
    { code: "JO", dialCode: "+962", timezone: "Asia/Amman" },
    { code: "JP", dialCode: "+81", timezone: "Asia/Tokyo" },
    { code: "KE", dialCode: "+254", timezone: "Africa/Nairobi" },
    { code: "KG", dialCode: "+996", timezone: "Asia/Bishkek" },
    { code: "KH", dialCode: "+855", timezone: "Asia/Phnom_Penh" },
    { code: "KI", dialCode: "+686", timezone: "Pacific/Tarawa" },
    { code: "KM", dialCode: "+269", timezone: "Indian/Comoro" },
    { code: "KN", dialCode: "+1869", timezone: "America/St_Kitts" },
    { code: "KP", dialCode: "+850", timezone: "Asia/Pyongyang" },
    { code: "KR", dialCode: "+82", timezone: "Asia/Seoul" },
    { code: "KW", dialCode: "+965", timezone: "Asia/Kuwait" },
    { code: "KY", dialCode: "+1345", timezone: "America/Cayman" },
    { code: "KZ", dialCode: "+7", timezone: "Asia/Almaty" },
    { code: "LA", dialCode: "+856", timezone: "Asia/Vientiane" },
    { code: "LB", dialCode: "+961", timezone: "Asia/Beirut" },
    { code: "LC", dialCode: "+1758", timezone: "America/St_Lucia" },
    { code: "LI", dialCode: "+423", timezone: "Europe/Vaduz" },
    { code: "LK", dialCode: "+94", timezone: "Asia/Colombo" },
    { code: "LR", dialCode: "+231", timezone: "Africa/Monrovia" },
    { code: "LS", dialCode: "+266", timezone: "Africa/Maseru" },
    { code: "LT", dialCode: "+370", timezone: "Europe/Vilnius" },
    { code: "LU", dialCode: "+352", timezone: "Europe/Luxembourg" },
    { code: "LV", dialCode: "+371", timezone: "Europe/Riga" },
    { code: "LY", dialCode: "+218", timezone: "Africa/Tripoli" },
    { code: "MA", dialCode: "+212", timezone: "Africa/Casablanca" },
    { code: "MC", dialCode: "+377", timezone: "Europe/Monaco" },
    { code: "MD", dialCode: "+373", timezone: "Europe/Chisinau" },
    { code: "ME", dialCode: "+382", timezone: "Europe/Podgorica" },
    { code: "MF", dialCode: "+590", timezone: "America/Marigot" },
    { code: "MG", dialCode: "+261", timezone: "Indian/Antananarivo" },
    { code: "MH", dialCode: "+692", timezone: "Pacific/Majuro" },
    { code: "MK", dialCode: "+389", timezone: "Europe/Skopje" },
    { code: "ML", dialCode: "+223", timezone: "Africa/Bamako" },
    { code: "MM", dialCode: "+95", timezone: "Asia/Yangon" },
    { code: "MN", dialCode: "+976", timezone: "Asia/Ulaanbaatar" },
    { code: "MO", dialCode: "+853", timezone: "Asia/Macau" },
    { code: "MP", dialCode: "+1670", timezone: "Pacific/Saipan" },
    { code: "MQ", dialCode: "+596", timezone: "America/Martinique" },
    { code: "MR", dialCode: "+222", timezone: "Africa/Nouakchott" },
    { code: "MS", dialCode: "+1664", timezone: "America/Montserrat" },
    { code: "MT", dialCode: "+356", timezone: "Europe/Malta" },
    { code: "MU", dialCode: "+230", timezone: "Indian/Mauritius" },
    { code: "MV", dialCode: "+960", timezone: "Indian/Maldives" },
    { code: "MW", dialCode: "+265", timezone: "Africa/Blantyre" },
    { code: "MX", dialCode: "+52", timezone: "America/Mexico_City" },
    { code: "MY", dialCode: "+60", timezone: "Asia/Kuala_Lumpur" },
    { code: "MZ", dialCode: "+258", timezone: "Africa/Maputo" },
    { code: "NA", dialCode: "+264", timezone: "Africa/Windhoek" },
    { code: "NC", dialCode: "+687", timezone: "Pacific/Noumea" },
    { code: "NE", dialCode: "+227", timezone: "Africa/Niamey" },
    { code: "NF", dialCode: "+672", timezone: "Pacific/Norfolk" },
    { code: "NG", dialCode: "+234", timezone: "Africa/Lagos" },
    { code: "NI", dialCode: "+505", timezone: "America/Managua" },
    { code: "NL", dialCode: "+31", timezone: "Europe/Amsterdam" },
    { code: "NO", dialCode: "+47", timezone: "Europe/Oslo" },
    { code: "NP", dialCode: "+977", timezone: "Asia/Kathmandu" },
    { code: "NR", dialCode: "+674", timezone: "Pacific/Nauru" },
    { code: "NU", dialCode: "+683", timezone: "Pacific/Niue" },
    { code: "NZ", dialCode: "+64", timezone: "Pacific/Auckland" },
    { code: "OM", dialCode: "+968", timezone: "Asia/Muscat" },
    { code: "PA", dialCode: "+507", timezone: "America/Panama" },
    { code: "PE", dialCode: "+51", timezone: "America/Lima" },
    { code: "PF", dialCode: "+689", timezone: "Pacific/Tahiti" },
    { code: "PG", dialCode: "+675", timezone: "Pacific/Port_Moresby" },
    { code: "PH", dialCode: "+63", timezone: "Asia/Manila" },
    { code: "PK", dialCode: "+92", timezone: "Asia/Karachi" },
    { code: "PL", dialCode: "+48", timezone: "Europe/Warsaw" },
    { code: "PM", dialCode: "+508", timezone: "America/Miquelon" },
    { code: "PN", dialCode: "+64", timezone: "Pacific/Pitcairn" },
    { code: "PR", dialCode: "+1787", timezone: "America/Puerto_Rico" },
    { code: "PS", dialCode: "+970", timezone: "Asia/Gaza" },
    { code: "PT", dialCode: "+351", timezone: "Europe/Lisbon" },
    { code: "PW", dialCode: "+680", timezone: "Pacific/Palau" },
    { code: "PY", dialCode: "+595", timezone: "America/Asuncion" },
    { code: "QA", dialCode: "+974", timezone: "Asia/Qatar" },
    { code: "RE", dialCode: "+262", timezone: "Indian/Reunion" },
    { code: "RO", dialCode: "+40", timezone: "Europe/Bucharest" },
    { code: "RS", dialCode: "+381", timezone: "Europe/Belgrade" },
    { code: "RU", dialCode: "+7", timezone: "Europe/Moscow" },
    { code: "RW", dialCode: "+250", timezone: "Africa/Kigali" },
    { code: "SA", dialCode: "+966", timezone: "Asia/Riyadh" },
    { code: "SB", dialCode: "+677", timezone: "Pacific/Guadalcanal" },
    { code: "SC", dialCode: "+248", timezone: "Indian/Mahe" },
    { code: "SD", dialCode: "+249", timezone: "Africa/Khartoum" },
    { code: "SE", dialCode: "+46", timezone: "Europe/Stockholm" },
    { code: "SG", dialCode: "+65", timezone: "Asia/Singapore" },
    { code: "SH", dialCode: "+290", timezone: "Atlantic/St_Helena" },
    { code: "SI", dialCode: "+386", timezone: "Europe/Ljubljana" },
    { code: "SJ", dialCode: "+47", timezone: "Arctic/Longyearbyen" },
    { code: "SK", dialCode: "+421", timezone: "Europe/Bratislava" },
    { code: "SL", dialCode: "+232", timezone: "Africa/Freetown" },
    { code: "SM", dialCode: "+378", timezone: "Europe/San_Marino" },
    { code: "SN", dialCode: "+221", timezone: "Africa/Dakar" },
    { code: "SO", dialCode: "+252", timezone: "Africa/Mogadishu" },
    { code: "SR", dialCode: "+597", timezone: "America/Paramaribo" },
    { code: "SS", dialCode: "+211", timezone: "Africa/Juba" },
    { code: "ST", dialCode: "+239", timezone: "Africa/Sao_Tome" },
    { code: "SV", dialCode: "+503", timezone: "America/El_Salvador" },
    { code: "SX", dialCode: "+1721", timezone: "America/Lower_Princes" },
    { code: "SY", dialCode: "+963", timezone: "Asia/Damascus" },
    { code: "SZ", dialCode: "+268", timezone: "Africa/Mbabane" },
    { code: "TC", dialCode: "+1649", timezone: "America/Grand_Turk" },
    { code: "TD", dialCode: "+235", timezone: "Africa/Ndjamena" },
    { code: "TF", dialCode: "+262", timezone: "Indian/Kerguelen" },
    { code: "TG", dialCode: "+228", timezone: "Africa/Lome" },
    { code: "TH", dialCode: "+66", timezone: "Asia/Bangkok" },
    { code: "TJ", dialCode: "+992", timezone: "Asia/Dushanbe" },
    { code: "TK", dialCode: "+690", timezone: "Pacific/Fakaofo" },
    { code: "TL", dialCode: "+670", timezone: "Asia/Dili" },
    { code: "TM", dialCode: "+993", timezone: "Asia/Ashgabat" },
    { code: "TN", dialCode: "+216", timezone: "Africa/Tunis" },
    { code: "TO", dialCode: "+676", timezone: "Pacific/Tongatapu" },
    { code: "TR", dialCode: "+90", timezone: "Europe/Istanbul" },
    { code: "TT", dialCode: "+1868", timezone: "America/Port_of_Spain" },
    { code: "TV", dialCode: "+688", timezone: "Pacific/Funafuti" },
    { code: "TW", dialCode: "+886", timezone: "Asia/Taipei" },
    { code: "TZ", dialCode: "+255", timezone: "Africa/Dar_es_Salaam" },
    { code: "UA", dialCode: "+380", timezone: "Europe/Kyiv" },
    { code: "UG", dialCode: "+256", timezone: "Africa/Kampala" },
    { code: "UM", dialCode: "+1", timezone: "Pacific/Midway" },
    { code: "US", dialCode: "+1", timezone: "America/New_York" },
    { code: "UY", dialCode: "+598", timezone: "America/Montevideo" },
    { code: "UZ", dialCode: "+998", timezone: "Asia/Samarkand" },
    { code: "VA", dialCode: "+379", timezone: "Europe/Vatican" },
    { code: "VC", dialCode: "+1784", timezone: "America/St_Vincent" },
    { code: "VE", dialCode: "+58", timezone: "America/Caracas" },
    { code: "VG", dialCode: "+1284", timezone: "America/Tortola" },
    { code: "VI", dialCode: "+1340", timezone: "America/St_Thomas" },
    { code: "VN", dialCode: "+84", timezone: "Asia/Ho_Chi_Minh" },
    { code: "VU", dialCode: "+678", timezone: "Pacific/Efate" },
    { code: "WF", dialCode: "+681", timezone: "Pacific/Wallis" },
    { code: "WS", dialCode: "+685", timezone: "Pacific/Apia" },
    { code: "YE", dialCode: "+967", timezone: "Asia/Aden" },
    { code: "YT", dialCode: "+262", timezone: "Indian/Mayotte" },
    { code: "ZA", dialCode: "+27", timezone: "Africa/Johannesburg" },
    { code: "ZM", dialCode: "+260", timezone: "Africa/Lusaka" },
    { code: "ZW", dialCode: "+263", timezone: "Africa/Harare" },
];

const BY_CODE = new Map(COUNTRIES.map((country) => [country.code, country]));

export function findCountry(code: string | undefined): Country | undefined {
    return code ? BY_CODE.get(code.trim().toUpperCase()) : undefined;
}

/** The country's name in `language`, falling back to its code. */
export function countryName(code: string, language?: string): string {
    try {
        const names = new Intl.DisplayNames(language ? [language] : undefined, {
            type: "region",
        });
        return names.of(code) ?? code;
    } catch {
        // A runtime without `DisplayNames`, or a language tag it rejects. A
        // bare code is worse than a name and far better than a blank option.
        return code;
    }
}

/** Every country, labelled and sorted for the language on screen. */
export function sortedCountries(
    language?: string
): { code: string; name: string }[] {
    const collator = new Intl.Collator(language);
    return COUNTRIES.map(({ code }) => ({
        code,
        name: countryName(code, language),
    })).sort((a, b) => collator.compare(a.name, b.name));
}

/** The zone to suggest for a country, or undefined when it is not one we know. */
export function timezoneForCountry(code: string | undefined): string | undefined {
    return findCountry(code)?.timezone;
}

/** The dialling code for a country, or undefined. */
export function dialCodeForCountry(code: string | undefined): string | undefined {
    return findCountry(code)?.dialCode;
}
